import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
  CurecallButton,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { saveFollowup, TMedEventForm } from 'src/components/followup/generic-followup-form.service'
import { useTranslation } from 'react-i18next'
import MedEventFields from 'src/components/med-events/med-event-fields.component'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPatientFollowup } from 'src/types/followup'
import { TCompletedMedEvent } from '../followup/generic-followup-form.type'
import { useQueryClient } from '@tanstack/react-query'
import { getErrorMessage } from 'src/utils/http.util'
import { useNotification } from 'src/providers/notification.provider'
import { TPlanningPatientMedEvent } from 'src/pages/medical/planning/_components/planning.type'
import { useMedEventModalContext } from './med-event-modal.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { TLabel } from 'src/types/locales'

export type TPatientMedEventForForm = {
  id: number
  date?: string | Date
  convocationDate?: string | Date | null
  MedicalStaffMemberId?: number
  LocationId?: number
  MedEvent?: { id: number; intlName?: TLabel }
  PatientFollowup?: { id?: number }
  PatientFollowupId?: number
}

interface Props {
  patient: TPatient
  patientMedEventForForm: TPatientMedEventForForm
  onToggle: (event?: React.MouseEvent<HTMLElement>, isSubmittingForm?: boolean) => void
  onDelete?: () => void
}
const MedEventForm = ({ patient, patientMedEventForForm, onToggle, onDelete }: Props) => {
  const { t } = useTranslation()
  const { getFollowupById, activeDoctors, getMedEventLabelById } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const { setHasFormBeenModified } = useMedEventModalContext()

  const correspondingPatientFollowup = patient.PatientFollowups?.find(
    (pf) => pf.id === patientMedEventForForm.PatientFollowup?.id || patientMedEventForForm.PatientFollowupId,
  ) as TPatientFollowup
  const correspondingFollowup = getFollowupById(correspondingPatientFollowup?.FollowupId)
  const medEventUiToShow = correspondingFollowup?.configUI.medEventsUI.find((me) => {
    return me.MedEvent.id === patientMedEventForForm?.MedEvent?.id
  })

  const methods = useForm<TMedEventForm>({
    defaultValues: {
      date: patientMedEventForForm?.date ? new Date(patientMedEventForForm?.date) : undefined,
      UserId: patientMedEventForForm?.MedicalStaffMemberId,
      LocationId: patientMedEventForForm?.LocationId,
      convocationDate: patientMedEventForForm?.convocationDate
        ? new Date(patientMedEventForForm?.convocationDate)
        : undefined,
    },
  })

  const watchedValues = methods.watch()

  useEffect(() => {
    if (setHasFormBeenModified) {
      const hasChanged =
        watchedValues.date?.toString() !== patientMedEventForForm?.date?.toString() ||
        watchedValues.UserId !== patientMedEventForForm?.MedicalStaffMemberId ||
        watchedValues.LocationId !== patientMedEventForForm?.LocationId ||
        watchedValues.convocationDate?.toString() !== patientMedEventForForm?.convocationDate?.toString()

      setHasFormBeenModified(hasChanged)
    }
  }, [watchedValues, patientMedEventForForm, setHasFormBeenModified])

  async function onSubmit(values: TMedEventForm) {
    try {
      if (setHasFormBeenModified) {
        setHasFormBeenModified(false)
      }
      const newCompletedMedEvent: TCompletedMedEvent = {
        LocationId: values.LocationId && +values.LocationId,
        UserId: values.UserId && +values.UserId,
        date: values.date,
        medEventUI: medEventUiToShow,
        patientMedEventId: patientMedEventForForm.id,
        convocationDate: values.convocationDate,
      }

      const patientUpdatedResult = await saveFollowup({
        patientVariables: [],
        completedMedEvents: [newCompletedMedEvent],
        deletedPatientMedEventIds: [],
        patient,
        followupId: correspondingPatientFollowup.FollowupId,
        patientFollowupId: correspondingPatientFollowup.id,
      })

      // update planning
      queryClient
        .getQueriesData({ queryKey: ['planning-patient-med-events'] })
        .forEach(async ([queryKey, queryData]: any) => {
          const planningPatientMedEvents = queryData?.patientMedEvents || []
          const hasAPatientMedEventOfListBeenUpdated = planningPatientMedEvents.some(
            (planningPatientMedEvent: TPlanningPatientMedEvent) =>
              patientUpdatedResult.PatientMedEvents.some(
                (pme: TPlanningPatientMedEvent) => pme.id === planningPatientMedEvent.id,
              ),
          )
          if (hasAPatientMedEventOfListBeenUpdated) {
            await queryClient.invalidateQueries(queryKey)
          }
        })
      onToggle(undefined, true)
    } catch (error) {
      notify({
        title: t('components.med-events-form.error-message'),
        message: getErrorMessage(error),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  if (!medEventUiToShow) return null

  return (
    <FormProvider {...methods}>
      <form method="POST" action="#" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="c-px-4 c-py-6 c-bg-grey-50 c-rounded-md c-w-full">
          <h2 className="c-text-xl c-text-grey-700 c-font-bold c-mb-5">
            {getMedEventLabelById(medEventUiToShow.MedEvent.id)}
          </h2>
          <MedEventFields selectedMedEvent={medEventUiToShow} patient={patient} isEditing={true} />
          <div
            className={twMerge(
              'c-flex c-justify-end c-gap-8 c-items-end c-mt-4',
              activeDoctors?.length === 0 &&
                medEventUiToShow.properties.some((prop) => prop.fieldName === 'UserId') &&
                'c-mt-14',
            )}
          >
            <CurecallButton
              onClick={() => {
                onToggle?.()
              }}
              appearance={ECurecallButtonAppearance.CANCEL}
            >
              {t('common.actions.cancel')}
            </CurecallButton>
            {onDelete && (
              <CurecallButton onClick={onDelete} appearance={ECurecallButtonAppearance.ALERT}>
                {t('components.med-events-form.delete')}
              </CurecallButton>
            )}
            <CurecallButton type="submit" appearance={ECurecallButtonAppearance.SECONDARY}>
              {t('components.generic-followups-form.validate-changes')}
            </CurecallButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default MedEventForm
